import {
  AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, effect,
  ElementRef, inject, model, OnDestroy, OnInit, signal, untracked, ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { Subject, takeUntil } from 'rxjs';
import { FikoYTIstekKok } from '@/src/service/modules/fikoyt-istek-kok/fikoyt.istek.makinesi.service';
import { KartWebSocketService } from '../services/kart-websocket.service';
import { AvatarService } from '@/src/app/servisler/avatar.service';
import { FikoYTService } from '@/src/service/fikoyt.service';

export interface IKartSahibiOlay {
  _id?: string;
  zaman: string;
  durum: 'koyuldu' | 'cekildi';
  manuel?: boolean;
  /** Görünüm amaçlı: bu satır, ≤5sn içindeki birden çok ham kaydı temsil ediyorsa adet ve alt kayıtlar */
  grupAdedi?: number;
  grupOlaylari?: IKartSahibiOlay[];
}
/** Çoklu giriş/çıkış modunda bir güne ait tek bir mesai bloğu (giriş→çıkış çifti). */
export interface IGirisCikisSegment {
  /** Girişin ISO zamanı (tolerans uygulanmışsa yuvarlanmış hâli) */
  girisZaman: string;
  /** Çıkışın ISO zamanı — segment henüz kapanmadıysa '' */
  cikisZaman: string;
  /** 'HH:MM' — görüntüleme */
  giris: string;
  /** 'HH:MM' — görüntüleme, açık segmentte '' */
  cikis: string;
  girisYuvari: boolean;
  cikisYuvari: boolean;
  /** true = bu segmentin çıkışı henüz okutulmadı (gün içinde hâlâ "içeride") */
  acikMi: boolean;
  /** Giriş-çıkış farkı 'SA:DK:SN', açık segmentte '' */
  sure: string;
}

export interface IKartSahibiGun {
  tarih: string;
  gun: number;
  /** Kısa gün adı — 'Pzt', 'Sal' vb. (hafta/ay listesinde hızlı taranabilirlik için) */
  gunAdi: string;
  /** Bugünün satırı mı — vurgulama için */
  bugunMu: boolean;
  ilk: string;
  son: string;
  /** Giriş/çıkış saati tolerans ile hedef saate yuvarlandıysa true (bilgilendirme ikonu için). */
  ilkYuvari?: boolean;
  sonYuvari?: boolean;
  /** İlk okutma ile son okutma arasındaki fark — 'SA:DK:SN' biçiminde, tekil/boş günlerde '' */
  sure: string;
  sayisi: number;
  olaylar: IKartSahibiOlay[];
  haftaSonuMu: boolean;
  tatilMi: boolean;
  aciklama: string;      // gün notu — boş string ise yok
  /** DB'deki meta kaydının _id'si — güncelleme/silme için */
  metaId?: string;
  /** Bu güne ait mola/izin kesintileri */
  kesintiler: IGunKesinti[];
  /** Ham süreden 'mola' türü kesintiler düşülmüş hâli — 'SA:DK' biçiminde, hesaplanamıyorsa '' */
  netSure: string;
  /** Çoklu giriş/çıkış modu açıksa doldurulur (bkz. cokluGirisCikisAcik); kapalıysa boş dizi. */
  segmentler: IGirisCikisSegment[];
}

/** pdks_gun_kesintiler koleksiyonu belgesi — bir güne ait mola/izin aralığı.
 *  tur === 'mola' ise net süreden düşülür, 'izin' ise düşülmez (sadece kayıt amaçlı). */
export interface IGunKesinti {
  _id?: string;
  kart_kimlik: string;
  tarih: string;          // 'YYYY-MM-DD'
  tur: 'mola' | 'izin';
  baslangic: string;      // 'HH:MM'
  bitis: string;          // 'HH:MM'
  aciklama?: string;      // serbest metin not — boş/yok olabilir
}

/** pdks_gun_meta koleksiyonu belgesi — kart_kimlik + tarih benzersiz key */
export interface IGunMeta {
  _id?: string;
  kart_kimlik: string;
  tarih: string;         // 'YYYY-MM-DD'
  tatilMi: boolean;
  aciklama?: string;     // güne ait serbest metin notu
}

/** Hızlı ekleme (tek tık) şablonu — sabit bir mola/izin aralığını güne ekler. */
export interface IKesintiSablon {
  id: string;
  ad: string;
  tur: 'mola' | 'izin';
  baslangic: string; // 'HH:MM'
  bitis: string;     // 'HH:MM'
  aciklama?: string;
}

const AY_ISIMLERI = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];

/** Kısa gün adları — Date.getDay() indeksiyle (0=Pazar) hizalı. */
const GUN_ISIMLERI_KISA = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];

/** Varsayılan hızlı mola/izin şablonları — tek tıkla güne eklenir, dilediğiniz gibi genişletilebilir. */
const KESINTI_SABLONLARI: IKesintiSablon[] = [
  { id: 'sabah-cayi', ad: 'Sabah çayı', tur: 'mola', baslangic: '10:00', bitis: '10:10', aciklama: 'Sabah çayı' },
  { id: 'ogle-yemegi', ad: 'Öğle yemeği', tur: 'mola', baslangic: '12:00', bitis: '12:40', aciklama: 'Öğle yemeği' },
  { id: 'aksam-cayi', ad: 'Akşam çayı', tur: 'mola', baslangic: '15:00', bitis: '15:10', aciklama: 'Akşam çayı' },
];

export interface IOkutmaGrubu {
  /** Grubun ilk okutması — giriş saati olarak gösterilir */
  ilkOlay: IKartSahibiOlay;
  /** Grubun son okutması — çıkış saati olarak gösterilir (tekil gruplarda ilkOlay ile aynı) */
  sonOlay: IKartSahibiOlay;
  /** Gruptaki tüm ham okutmalar */
  tumOlaylar: IKartSahibiOlay[];
  /** Tekil mi (1 okutma) yoksa gerçek grup mu */
  grupMu: boolean;
}

/** Paylaşım sisteminde gösterilecek kart — bir kartın aylık okuma geçmişi */
@Component({
  selector: 'kart-sahibi-paylasim-kart',
  standalone: true,
  imports: [CommonModule, FormsModule, NzPopconfirmModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './kart-sahibi-kart.component.html',
  styleUrl: './kart-sahibi-kart.component.less',
})
export class KartSahibiKartComponent implements OnInit, AfterViewInit, OnDestroy {
  parametreler = model<any>();

  private cd = inject(ChangeDetectorRef);
  private wsService = inject(KartWebSocketService);
  private avatar = inject(AvatarService);
  private fikoyt = inject(FikoYTService);
  private _destroy$ = new Subject<void>();
  private _dialogOverlayEl: HTMLElement | null = null;
  private _ayarDialogOverlayEl: HTMLElement | null = null;

  yukleniyor = signal(false);
  seciliTarih = signal(new Date());
  gunler = signal<IKartSahibiGun[]>([]);
  /** En son çekilen ham olaylar — tolerans/ayar değişince yeniden gruplamak için. */
  private _hamOlaylar: any[] = [];
  /** Bu kart şu an okuyucuda mı? — kart-okuyucu-kart'tan aktifKartUid$ ile güncellenir */
  kartOkuyucuda = signal(false);

  /** Kişinin şu an firmada olup olmadığı.
   *  Okuyucuda ise kesinlikle içeride. Değilse bugünün segment/olay yapısına bakılır:
   *  - Çoklu mod açık: son segmentin acikMi (çıkışı henüz okutulmamış) = firmada
   *  - Çoklu mod kapalı: bugün ilk okuma var ama son okuma yok = firmada (tek giriş)
   *  null = bugün hiç hareket yok (bilinmiyor, etiket gösterilmez). */
  firmadaMi = computed<boolean | null>(() => {
    if (this.kartOkuyucuda()) return true;
    const bugun = new Date();
    const bugunStr = `${bugun.getFullYear()}-${String(bugun.getMonth() + 1).padStart(2, '0')}-${String(bugun.getDate()).padStart(2, '0')}`;
    const bugunGun = this.gunler().find(g => g.tarih === bugunStr);
    if (!bugunGun || bugunGun.olaylar.length === 0) return false;
    if (this.cokluGirisCikisAcik()) {
      // Segment bazlı: son segmentin açık olması = hâlâ içeride
      if (!bugunGun.segmentler.length) return null;
      return bugunGun.segmentler.at(-1)!.acikMi;
    } else {
      // Tekli mod: son okutmanın grubuna bakılır.
      // Gruplama açıksa okutmaGruplari() ile, kapalıysa ham olaylara bakılır.
      const gruplari = this.okutmaGruplari(bugunGun.olaylar);
      if (!gruplari.length) return null;
      // Tek grup → sadece giriş var (henüz çıkış yok) = firmada
      // Çift+ grup → son grup çıkış = dışarıda; giriş süreci açık kalmış
      // Gerçek kural: grup sayısı tek ise son giriş yapılmış çıkış yok = içeride
      return gruplari.length % 2 === 1;
    }
  });
  /** Okuyucu durumu periyodik DB kontrolü için interval kimliği */
  private _okuyucuPollInterval: any = null;
  /** Canlı oturumda bu kart için 'koyuldu' olayı görüldü mü? — eski DB kayıtlarına
   *  güvenilmesi engellenir: kart çekilmiş ama cekildi kaydı DB'ye yazılamamışsa,
   *  açılışta eski 'koyuldu' kaydı rozeti yanlış açmasın diye */
  private _canliKoyuldu = false;
  dialogGun = signal<IKartSahibiGun | null>(null);
  tatilKaydediliyor = signal(false);
  aciklamaKaydediliyor = signal(false);
  /** Dialog'daki açıklama textarea değeri — blur'da kaydedilir */
  aciklamaDraft = signal('');

  /** Ay için yüklenen meta kayıtları — tarih→IGunMeta */
  private _gunMetaMap = new Map<string, IGunMeta>();
  /** Ay için yüklenen mola/izin kayıtları — tarih→IGunKesinti[] */
  private _gunKesintiMap = new Map<string, IGunKesinti[]>();

  // Manuel kayıt formu (gün dialogu içinde)
  manuelFormAcik = signal(false);
  manuelTarih = signal('');
  manuelSaat = signal('');
  manuelDurum = signal<'koyuldu' | 'cekildi'>('koyuldu');
  manuelKaydediliyor = signal(false);

  // Mola/izin (kesinti) formu (gün dialogu içinde)
  kesintiFormAcik = signal(false);
  kesintiTur = signal<'mola' | 'izin'>('mola');
  kesintiBaslangic = signal('');
  kesintiBitis = signal('');
  kesintiAciklama = signal('');
  kesintiKaydediliyor = signal(false);
  /** Düzenlenen kesintinin _id'si — dolu ise form düzenleme modunda */
  duzenlenenKesintiId = signal<string | null>(null);
  /** Hızlı mola/izin şablonları — tek tıkla güne varsayılan aralık ekler. */
  readonly kesintiSablonlari = KESINTI_SABLONLARI;

  // Kayıt düzenleme (manuel + — süper yönetici için — cihaz kayıtları)
  duzenlenenOlayId = signal<string | null>(null);
  duzenleTarih = signal('');
  duzenleSaat = signal('');
  duzenleDurum = signal<'koyuldu' | 'cekildi'>('koyuldu');
  duzenleKaydediliyor = signal(false);

  /** Kullanıcıya özel gruplama eşiği — saniye cinsinden; localStorage'da kart UID bazlı saklanır.
   *  0 = gruplama kapalı. Varsayılan 5 saniye. */
  grupEsikSaniye = signal<number>(5);
  /** Gruplama seçeneği açık mı */
  grupOkutmaAcik = signal(false);

  /** Çoklu giriş/çıkış modu — açıksa günün (eşiklenmiş) okutmaları sırayla
   *  giriş/çıkış/giriş/çıkış... sayılır (izin/mola sonrası dönüşler dahil, birden
   *  fazla mesai bloğu). Kapalıysa: sadece ilk okutma giriş, son okutma çıkış sayılır
   *  (eski/varsayılan davranış). localStorage'da kart UID bazlı saklanır. */
  cokluGirisCikisAcik = signal(false);

  /** Günlük hedef çalışma süresi 'HH:MM' — localStorage'da kart UID bazlı saklanır.
   *  Boş/'' = hesap kapalı. Buna göre gün bazında ve aylık fazla/eksik çalışma hesaplanır. */
  gunlukHedefSaat = signal('');

  /** Giriş toleransı — hedef giriş saatine yuvarlanacak okunan girişler için. */
  girisHedefSaat = signal('');
  /** Çıkış toleransı — hedef çıkış saatine yuvarlanacak okunan çıkışlar için. */
  cikisHedefSaat = signal('');
  /** Giriş/çıkış tolerans farkı (dakika). Hedef saat tanımlı değilse opsiyonel devre dışı kalır. */
  toleransDk = signal(15);

  // Grup düzenleme state'i — bir grupta ilk/son zamanını düzenleme
  duzenlenenGrupIlkId = signal<string | null>(null);
  duzenlenenGrupSonId = signal<string | null>(null);
  duzenleGrupIlkSaat = signal('');
  duzenleGrupSonSaat = signal('');
  duzenleGrupTarih = signal('');
  duzenleGrupKaydediliyor = signal(false);

  // Dialog, kartın (paylaşım sisteminde transform içeren üst elemanların)
  // içinde kalmasın diye açılır açılmaz document.body'e taşınıyor —
  // icerikler.component.ts'teki lightbox ile AYNI yöntem: statik ViewChild +
  // ngAfterViewInit'te TEK SEFERLİK taşıma. (Önceki sürüm bir setter kullanıyordu;
  // bu, her CD turunda tekrar tetiklenebiliyor ve ayar dialogu hiç temizlenmiyordu —
  // kart feed'de yok edilip yeniden oluşturulunca body'de öksüz overlay birikip
  // sayfayı görünmez bir tam ekran tıklama-yutucuya çeviriyordu.)
  @ViewChild('dialogOverlay', { static: true }) private _dialogOverlayRef!: ElementRef<HTMLElement>;

  // ─── Ayarlar dialog'u — dişli butonuyla açılır ──────────────────────────────
  /** Günlük hedef + tolerans + gruplama ayarlarının bulunduğu modal dialog */
  ayarDialogAcik = signal(false);

  @ViewChild('ayarDialogOverlay', { static: true }) private _ayarDialogOverlayRef!: ElementRef<HTMLElement>;

  ngAfterViewInit(): void {
    if (this._dialogOverlayRef?.nativeElement) {
      this._dialogOverlayEl = this._dialogOverlayRef.nativeElement;
      if (!document.body.contains(this._dialogOverlayEl)) {
        document.body.appendChild(this._dialogOverlayEl);
      }
    }
    if (this._ayarDialogOverlayRef?.nativeElement) {
      this._ayarDialogOverlayEl = this._ayarDialogOverlayRef.nativeElement;
      if (!document.body.contains(this._ayarDialogOverlayEl)) {
        document.body.appendChild(this._ayarDialogOverlayEl);
      }
    }
  }

  ayarlarAc() {
    this.ayarDialogAcik.set(true);
    // gunAc() ile aynı gerekçe: feed'de sonradan eklenen kartların overlay'leri
    // araya girmiş olabilir — en üstte kalması için en sona taşı.
    if (this._ayarDialogOverlayEl) {
      document.body.appendChild(this._ayarDialogOverlayEl);
    }
    this.cd.markForCheck();
  }

  ayarlarKapat() {
    this.ayarDialogAcik.set(false);
    this.cd.markForCheck();
  }

  get sahipIsmi(): string {
    return this.parametreler()?.ek?.sahipIsmi || 'İsimsiz Kart';
  }
  get kartUid(): string {
    return this.parametreler()?.ek?.kartUid || '';
  }
  /** Profil resmi GridFS id'si — kart-sahibi-edit'te yüklenir, ek.sahipResim olarak saklanır */
  get sahipResimId(): string {
    return this.parametreler()?.ek?.sahipResim || '';
  }
  /** parametreler() (input signal) degistiginde otomatik yeniden hesaplanir.
   *  Burada cache-bust gerekmez: sayfa acildiginda id zaten guncel gelir,
   *  ayni oturumda resim degisimi kart-sahibi-edit tarafinda yapilir. */
  sahipResimUrl = computed(() => this.avatar.profilResimUrl(this.parametreler()?.ek?.sahipResim || '', '128x128'));
  get ayBasligi(): string {
    const t = this.seciliTarih();
    return `${AY_ISIMLERI[t.getMonth()]} ${t.getFullYear()}`;
  }
  get buAymi(): boolean {
    const t = this.seciliTarih();
    const now = new Date();
    return t.getFullYear() === now.getFullYear() && t.getMonth() === now.getMonth();
  }
  /** Süper yönetici cihaz kayıtlarını da silebilsin diye — girişteki (web oturumu)
   *  kullanıcının kendi yetkilerine bakılır (bkz. icerikler.component.ts, aynı desen). */
  get superYoneticiMi(): boolean {
    const yetkiler = this.fikoyt.kimlik?.aktifKullaniciJson?.()?.yetkiler || [];
    return Array.isArray(yetkiler) && yetkiler.some((x: string) => x === 'Süper Yönetici' || x === 'Super Yonetici');
  }

  constructor() {
    // Ebeveyn (ikart-kutusu) parametreleri tazelediğinde (canlı güncelleme, sayfa
    // değişimi, kart yeniden yükleme) tolerans ayarlarını içerik `ek`'inden yeniden yükle.
    //
    // KRİTİK: _toleranslariYukle() -> _grupGuvence() -> _ayIcinYenidenGrupla() zinciri
    // dialogGun() sinyalini hem OKUYUP hem de (bir gün dialogu açıkken) YENİ bir referansla
    // YAZIYOR. untracked() olmadan bu effect dialogGun'a bağımlı hale geliyor; gün dialogu
    // açıldığı an effect kendi yazdığı dialogGun değişikliğiyle kendini yeniden tetikliyor
    // ve senkron sonsuz döngüye girip sayfayı kilitliyordu. untracked(): bu effect SADECE
    // parametreler()?.ek değişince çalışsın, içindeki okuma/yazmalar effect'in kendi
    // bağımlılık grafiğine eklenmesin.
    effect(() => {
      const ek = this.parametreler()?.ek;
      if (ek) untracked(() => this._toleranslariYukle());
    });
  }

  ngOnInit() {
    // Gruplama eşiği (grupEsikSaniye) artık localStorage'dan DEĞİL, aşağıdaki
    // _toleranslariYukle() içinde diğer tolerans alanlarıyla birlikte DB'den
    // (kart içeriğinin `ek` alanından) yüklenir.
    // Günlük hedef süre ayarını yükle (fazla/eksik çalışma hesabı için)
    this._hedefiniYukle();
    // Giriş/çıkış tolerans ayarlarını kart metadata'sından yükle
    this._toleranslariYukle();
    this.ayVerisiYukle();

    // Timer yerine: WebSocket'ten bu karta ait bir hareket geldiğinde anında yenile
    this.wsService.kartOlayi$
      .pipe(takeUntil(this._destroy$))
      .subscribe(olay => {
        if (!olay.kartKimlik || olay.kartKimlik !== this.kartUid) return;
        this.ayVerisiYukle();
      });

    // WS anlık takip — cihaz filtresinden bağımsız ham kart olayları akışı.
    // (kartOlayi$ başka bir widget'ın cihaz filtresine takılıp olayı yutabiliyordu)
    this.wsService.kartOlayiHam$
      .pipe(takeUntil(this._destroy$))
      .subscribe(olay => {
        const gelen = String(olay.kartKimlik || '').toUpperCase();
        const hedef = this.kartUid.toUpperCase();
        if (!gelen || !hedef) return;
        if (gelen !== hedef) return;
        if (olay.durum === 'koyuldu') {
          this.kartOkuyucuda.set(true);
          this._canliKoyuldu = true;
        } else if (olay.durum === 'cekildi') {
          this.kartOkuyucuda.set(false);
          this._canliKoyuldu = false;
        }
        this.cd.markForCheck();
      });

    // NOT: telemetri (istatistik.kart_takili/son_kart_uid) bilinçli olarak KULLANILMIYOR —
    // cihazlar kart çekildikten sonra da kart_takili=true + son okunan UID'yi raporlayabildiği
    // için belirteci yanlışlıkla açık tutuyordu. Doğruluk kaynağı: gerçek olaylar + DB son kaydı.

    // DB'den son hareket durumuna göre belirteci senkronize et (WS olayı kaçtıysa bile çalışır)
    this.okuyucuDurumuKontrol();
    // Periyodik kontrol — kart başka bir oturumda konulup/çekilse bile belirteç güncellenir
    this._okuyucuPollInterval = setInterval(() => this.okuyucuDurumuKontrol(), 5000);

    if (!this.wsService.bagli) {
      this.wsService.baglan();
    }
  }
  /**
   * Bu kartın okuyucuda olup olmadığını DB'den sorgular.
   * pdks_kart_hareketler koleksiyonundaki SON hareket kaydına bakılır:
   * son kayıt 'koyuldu' ise kart hâlâ okuyucudadır, 'cekildi' ise değildir.
   * WS olayı kaçmış olsa bile (sayfa yenileme, widget sonradan açılma) doğru çalışır.
   */
  async okuyucuDurumuKontrol(): Promise<void> {
    const uid = this.kartUid;
    if (!uid) return;
    try {
      const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.pdks_kart_hareketler.aggregate' });
      istek.parametreler = {
        aggregate: [
          { $match: {
              vt: 'FikoYT.sistem.cihaz.olaylar',
              durum: { $in: ['koyuldu', 'cekildi'] },
              kart_kimlik: uid,
          } },
          // _id (ObjectId) doğal zaman sıralıdır — sunucu_zamani eksik eski kayıtlarda da güvenlidir
          { $sort: { _id: -1 } },
          { $limit: 1 },
          { $project: { _id: 1, durum: 1, sunucu_zamani: 1 } },
        ],
      };
      await istek.isteSync();
      const sonuc: any[] = istek.cevap?.sonuc || (Array.isArray(istek.cevap) ? istek.cevap : []) || [];
      const son = sonuc?.[0];
      let okuyucuda = false;
      if (son?.durum === 'koyuldu') {
        // Kayıt yaşı kontrolü: kart çekilmiş ama cekildi kaydı DB'ye yazılamamışsa
        // (olay kaçmışsa) eski 'koyuldu' kaydı rozeti yanlış açmasın.
        // 1) Kayıt taze ise (son 2 dk) güvenilir, 2) değilse yalnızca canlı oturumda
        //    bu kartın koyuldu olayı canlı görüldüyse açık kalır.
        const zaman = son.sunucu_zamani ? new Date(son.sunucu_zamani).getTime() : 0;
        const yas = zaman > 0 ? Date.now() - zaman : Number.MAX_SAFE_INTEGER;
        okuyucuda = yas <= 120000 || this._canliKoyuldu;
      }
      if (this.kartOkuyucuda() !== okuyucuda) {
        this.kartOkuyucuda.set(okuyucuda);
        this.cd.markForCheck();
      }
    } catch {
      // sorgu başarısız — mevcut belirteç durumu korunur
    }
  }



  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
    // Okuyucu durumu periyodik kontrolünü durdur
    if (this._okuyucuPollInterval) { clearInterval(this._okuyucuPollInterval); this._okuyucuPollInterval = null; }
    // document.body'e taşınan overlay'ler component yok olurken yetim kalmasın —
    // ikisi de temizlenmeli (ayar dialogu daha önce hiç temizlenmiyordu, bkz. ngAfterViewInit yorumu).
    if (this._dialogOverlayEl?.parentNode) {
      this._dialogOverlayEl.parentNode.removeChild(this._dialogOverlayEl);
    }
    if (this._ayarDialogOverlayEl?.parentNode) {
      this._ayarDialogOverlayEl.parentNode.removeChild(this._ayarDialogOverlayEl);
    }
  }

  yenile() {
    this.ayVerisiYukle();
  }

  ayDegistir(delta: number) {
    const t = this.seciliTarih();
    this.seciliTarih.set(new Date(t.getFullYear(), t.getMonth() + delta, 1));
    this.ayVerisiYukle();
  }

  /** Ay görünümünde "Bugün" — seçili ayı içinde bulunulan aya döndürür. */
  ayBugune() {
    const now = new Date();
    this.seciliTarih.set(new Date(now.getFullYear(), now.getMonth(), 1));
    this.ayVerisiYukle();
  }

  gunAc(g: IKartSahibiGun) {
    this.dialogGun.set(g);
    this.aciklamaDraft.set(g.aciklama || '');
    this.manuelFormAcik.set(false);
    this.kesintiFormAcik.set(false);
    this.duzenlenenKesintiId.set(null);
    // Body'e taşınmış olsa bile, feed'de sonradan eklenen başka kartların
    // overlay'leri araya girmiş olabilir — her açılışta en sona taşıyarak
    // en üstte kalmasını garanti altına al.
    if (this._dialogOverlayEl) {
      document.body.appendChild(this._dialogOverlayEl);
    }
    this.cd.markForCheck();
  }

  dialogKapat() {
    this.dialogGun.set(null);
    this.manuelFormAcik.set(false);
    this.kesintiFormAcik.set(false);
    this.duzenlenenKesintiId.set(null);
    this.molaDialogAcik.set(false);
    this.cd.markForCheck();
  }

  // ─── Mola/İzin dialog'u — gün dialogu içindeki "Mola/İzin" tetikleyicisiyle
  // açılır. Gün dialogu zaten document.body'e taşınmış overlay içinde olduğundan
  // ayrı bir ViewChild/teleport gerekmiyor; iç içe sabit konumlu bir panel yeterli. */
  molaDialogAcik = signal(false);

  molaAc() {
    this.molaDialogAcik.set(true);
    this.cd.markForCheck();
  }

  molaKapat() {
    this.molaDialogAcik.set(false);
    this.kesintiFormAcik.set(false);
    this.duzenlenenKesintiId.set(null);
    this.cd.markForCheck();
  }

  saatFormat(isoStr: string): string {
    const d = new Date(isoStr);
    return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }

  gunTarihUzun(tarih: string): string {
    const d = new Date(tarih + 'T00:00:00');
    return isNaN(d.getTime()) ? tarih : d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', weekday: 'long' });
  }

  /** İki ISO zaman damgası arasındaki farkı 'SA:DK:SN' biçiminde döner (negatifse ''). */
  private sureFarkiHesapla(ilkIso: string, sonIso: string): string {
    const ilk = new Date(ilkIso).getTime();
    const son = new Date(sonIso).getTime();
    if (isNaN(ilk) || isNaN(son) || son < ilk) return '';
    const toplamSaniye = Math.floor((son - ilk) / 1000);
    const saat = Math.floor(toplamSaniye / 3600);
    const dakika = Math.floor((toplamSaniye % 3600) / 60);
    const saniye = toplamSaniye % 60;
    return `${String(saat).padStart(2, '0')}:${String(dakika).padStart(2, '0')}:${String(saniye).padStart(2, '0')}`;
  }

  /** Dakikayı 'SS:DD' biçiminde döner (negatif değerler 0'a sabitlenir). */
  dakikaFormat(dk: number): string {
    const guvenli = Math.max(0, Math.round(dk));
    const saat = Math.floor(guvenli / 60);
    const dakika = guvenli % 60;
    return `${String(saat).padStart(2, '0')}:${String(dakika).padStart(2, '0')}`;
  }

  /** 'SA:DK' veya 'SA:DK:SN' biçimindeki bir süre string'ini dakikaya çevirir. */
  sureToDakika(sure: string): number {
    if (!sure) return 0;
    const parcalar = sure.split(':').map(Number);
    if (parcalar.some(n => isNaN(n))) return 0;
    const [saat, dakika] = parcalar;
    return (saat || 0) * 60 + (dakika || 0);
  }

  /** Bir kesintinin (mola/izin) süresini dakika olarak döner — gece yarısını geçen aralıklar da desteklenir. */
  kesintiSureDk(k: IGunKesinti): number {
    const [bSaat, bDk] = (k.baslangic || '').split(':').map(Number);
    const [eSaat, eDk] = (k.bitis || '').split(':').map(Number);
    if ([bSaat, bDk, eSaat, eDk].some(n => isNaN(n))) return 0;
    let fark = (eSaat * 60 + eDk) - (bSaat * 60 + bDk);
    if (fark < 0) fark += 24 * 60; // ör. 23:30–00:15 gibi gece yarısını geçen kesinti
    return fark;
  }

  /** Bir günün kesinti listesindeki 'mola' türlerinin toplam dakikası (net süreden düşülen). */
  molaToplamDk(kesintiler: IGunKesinti[]): number {
    return (kesintiler || [])
      .filter(k => k.tur === 'mola')
      .reduce((acc, k) => acc + this.kesintiSureDk(k), 0);
  }

  /** Ham süreden (ilk-son arası) 'mola' türü kesintileri düşerek net süreyi 'SA:DK' biçiminde döner. */
  private netSureHesapla(ilkIso: string, sonIso: string, kesintiler: IGunKesinti[]): string {
    const ilk = new Date(ilkIso).getTime();
    const son = new Date(sonIso).getTime();
    if (isNaN(ilk) || isNaN(son) || son < ilk) return '';
    const toplamDk = Math.floor((son - ilk) / 60000);
    const netDk = toplamDk - this.molaToplamDk(kesintiler);
    return this.dakikaFormat(netDk);
  }

  // ─── Gruplama ayarı (kart içeriği — ek + DB kaydı) ──────────────────
  // localStorage KULLANILMIYOR — diğer tolerans alanlarıyla (girisHedefSaat,
  // cikisHedefSaat, toleransDk, cokluGirisCikisAcik) aynı desen: kişiye özel bu
  // ayar kart içerik kaydının `ek.grupEsikSaniye` alanında DB'de kalıcı tutulur.
  // Böylece firmada-panel gibi aynı kişinin DB kaydını okuyan başka bileşenler de
  // kart-sahibi ile HER ZAMAN aynı eşiği kullanır (bkz. _toleranslariYukle/_toleransUpsert/_toleransiDbyeYaz).

  grupAyariniKaydet(saniye: number) {
    const s = Math.max(0, Math.round(saniye));
    this.grupEsikSaniye.set(s);
    this.grupOkutmaAcik.set(s > 0);
    this._toleransUpsert();
    void this._toleransiDbyeYaz();
    this._grupGuvence();
  }

  grupToggle(acik: boolean) {
    if (!acik) {
      this.grupOkutmaAcik.set(false);
      this.grupEsikSaniye.set(0);
    } else {
      const mevcut = this.grupEsikSaniye();
      const hedef = mevcut > 0 ? mevcut : 5; // varsayılan 5 saniye
      this.grupEsikSaniye.set(hedef);
      this.grupOkutmaAcik.set(true);
    }
    this._toleransUpsert();
    void this._toleransiDbyeYaz();
    this._grupGuvence();
  }

  // ─── Çoklu giriş/çıkış modu ──────────────────
  // NOT: localStorage'da DEĞİL — girişHedefSaat/cikisHedefSaat/toleransDk ile aynı
  // yerde: kart içeriğinin `ek` alanında, DB'de kalıcı olarak saklanır (bkz.
  // _toleranslariYukle / _toleransUpsert / _toleransiDbyeYaz). Böylece TÜM
  // cihazlarda/oturumlarda aynı ayar geçerli olur.
  cokluGirisCikisDegistir(acik: boolean) {
    this.cokluGirisCikisAcik.set(acik);
    this._toleransUpsert();
    void this._toleransiDbyeYaz();
    this._grupGuvence();
  }

  // ─── Günlük hedef süre & fazla/eksik çalışma hesabı ──────────────────
  private _hedefKey(): string {
    return `ks_gunluk_hedef_${this.kartUid || 'global'}`;
  }

  private _hedefiniYukle() {
    try {
      const ham = localStorage.getItem(this._hedefKey());
      if (ham) this.gunlukHedefSaat.set(ham);
    } catch { }
  }

  /** Günlük hedef süreyi 'HH:MM' değerinden dakikaya çevirir; yoksa 0 (kapalı). */
  gunlukHedefDk(): number {
    const v = this.gunlukHedefSaat();
    if (!v) return 0;
    const [s, d] = v.split(':').map(Number);
    if (isNaN(s) || isNaN(d)) return 0;
    return s * 60 + d;
  }

  /** Hedef süre input'u değiştiğinde — localStorage'a yazar, anında yeniler. */
  hedefDegistir(v: string) {
    const deger = (v || '').trim();
    this.gunlukHedefSaat.set(deger);
    try { localStorage.setItem(this._hedefKey(), deger); } catch { }
    this.cd.markForCheck();
  }

  /** Bir günün net çalışma süresi ile hedef arasındaki fark (dakika). +fazla, −eksik. */
  gunFazlaEksikDk(g: IKartSahibiGun): number {
    const hedef = this.gunlukHedefDk();
    if (hedef <= 0) return 0;
    const net = this.sureToDakika(g.netSure);
    if (net <= 0) return 0; // net süre yoksa hesap etme
    return net - hedef;
  }

  /** '+02:15' / '−00:30' biçiminde işaretli fark metni (fark 0 ise boş). */
  gunFazlaEksikMetni(g: IKartSahibiGun): string {
    const f = this.gunFazlaEksikDk(g);
    if (f === 0) return '';
    return (f > 0 ? '+' : '−') + this.dakikaFormat(Math.abs(f));
  }

  /** Ay içinde net süresi hesaplanabilen (çalışılan) gün sayısı. */
  get ayCalisilanGunAdet(): number {
    return this.gunler().filter(g => !!g.netSure).length;
  }

  /** Ay içindeki tüm çalışılan günlerin net süre toplamı (dakika). */
  get ayToplamNetDk(): number {
    return this.gunler().reduce((a, g) => a + this.sureToDakika(g.netSure), 0);
  }

  /** Hedef aşılan toplam dakika (fazla çalışma). */
  get ayToplamFazlaDk(): number {
    if (this.gunlukHedefDk() <= 0) return 0;
    return this.gunler().reduce((a, g) => a + Math.max(0, this.gunFazlaEksikDk(g)), 0);
  }

  /** Hedefin altında kalınan toplam dakika (eksik çalışma). */
  get ayToplamEksikDk(): number {
    if (this.gunlukHedefDk() <= 0) return 0;
    return this.gunler().reduce((a, g) => a + Math.max(0, -this.gunFazlaEksikDk(g)), 0);
  }

  // ─── Giriş/çıkış toleransı (kart içeriği — ek + DB kaydı) ──────────────────
  // Ayar, kart içerik kaydının `ek` alanına yazılır ve DB'ye kalıcı olarak
  // FikoYT.sistem.DB.hareketler.guncelle ile kaydedilir (icerikler.component'teki
  // onUygulamaParametreDegisti deseniyle aynı). Böylece kart yenilendiğinde bile
  // korunur ve TÜM günler için geçerlidir.

  /** Tolerans alanlarını parametreler().ek'ten okur ve signal'lara aktarır. */
  private _toleranslariYukle() {
    const ek = this.parametreler()?.ek ?? {};
    if (typeof ek.girisHedefSaat === 'string') this.girisHedefSaat.set(ek.girisHedefSaat);
    if (typeof ek.cikisHedefSaat === 'string') this.cikisHedefSaat.set(ek.cikisHedefSaat);
    const n = Number(ek.toleransDk);
    if (!isNaN(n) && n >= 0) this.toleransDk.set(n);
    if (typeof ek.cokluGirisCikisAcik === 'boolean') this.cokluGirisCikisAcik.set(ek.cokluGirisCikisAcik);
    // Gruplama eşiği — localStorage DEĞİL, kart içeriğinin `ek` alanından (DB'den) okunur.
    // Böylece kişiye özel bu ayar tüm cihaz/oturumlarda ve firmada-panel gibi bu bilgiyi
    // DB'den okuyan diğer bileşenlerde de aynı kalır. Alan hiç yazılmamışsa (ilk açılış /
    // eski kayıt) varsayılan: 5sn, gruplama açık.
    if (typeof ek.grupEsikSaniye === 'number' && ek.grupEsikSaniye >= 0) {
      this.grupEsikSaniye.set(ek.grupEsikSaniye);
      this.grupOkutmaAcik.set(ek.grupEsikSaniye > 0);
    } else {
      this.grupEsikSaniye.set(5);
      this.grupOkutmaAcik.set(true);
    }
    this._grupGuvence();
  }

  /** Mevcut tolerans değerlerini kart içeriğinin `ek` alanına yazar.
   *  `icerik.ek` doğrudan mutasyonla güncellenir (ebeveynin dinamiKParametreler
   *  kopyası ile aynı referans olduğu için senkron kalır); `icerik` yoksa
   *  parametreler().ek kopyalanır ve model.set ile reaktivite tetiklenir. */
  private _toleransUpsert() {
    const params = this.parametreler() ?? {};
    const icerik: any = params.icerik;
    const hedefEk =
      icerik && typeof icerik === 'object' && icerik.ek && typeof icerik.ek === 'object'
        ? icerik.ek
        : { ...(params.ek ?? {}) };
    hedefEk['girisHedefSaat'] = this.girisHedefSaat();
    hedefEk['cikisHedefSaat'] = this.cikisHedefSaat();
    hedefEk['toleransDk'] = this.toleransDk();
    hedefEk['cokluGirisCikisAcik'] = this.cokluGirisCikisAcik();
    hedefEk['grupEsikSaniye'] = this.grupEsikSaniye();
    if (hedefEk !== params.ek) {
      this.parametreler.set({ ...params, ek: hedefEk });
    }
    this.cd.markForCheck();
  }

  async girisHedefDegistir(v: string) {
    this.girisHedefSaat.set((v || '').trim());
    this._toleransUpsert();
    await this._toleransiDbyeYaz();
    this._grupGuvence();
  }

  async cikisHedefDegistir(v: string) {
    this.cikisHedefSaat.set((v || '').trim());
    this._toleransUpsert();
    await this._toleransiDbyeYaz();
    this._grupGuvence();
  }

  async toleransDegistir(v: number | string) {
    const n = Math.max(0, Math.round(Number(v) || 0));
    this.toleransDk.set(n);
    this._toleransUpsert();
    await this._toleransiDbyeYaz();
    this._grupGuvence();
  }

  /** Tolerans alanlarını içerik kaydına (hareketler.guncelle) DB'de kalıcı olarak yazar. */
  private async _toleransiDbyeYaz() {
    const icerik: any = this.parametreler()?.icerik;
    if (!icerik?._id) {
      console.error('[KartSahibi] icerik _id bulunamadi — tolerans DB ye yazilamadi');
      return;
    }
    try {
      const istek = new FikoYTIstekKok({
        metod: 'POST',
        komut: 'FikoYT.sistem.DB.hareketler.guncelle',
        gecikmeliCevap: false,
      });
      istek.parametreler = {
        filter: { _id: icerik._id },
        set: {
          'ek.girisHedefSaat': this.girisHedefSaat(),
          'ek.cikisHedefSaat': this.cikisHedefSaat(),
          'ek.toleransDk': this.toleransDk(),
          'ek.cokluGirisCikisAcik': this.cokluGirisCikisAcik(),
          'ek.grupEsikSaniye': this.grupEsikSaniye(),
        },
      };
      await istek.isteSync();
    } catch (err) {
      console.error('[KartSahibi] tolerans ayari DB ye yazilamadi', err);
    }
  }
  private _grupGuvence() {
    try {
      this._ayIcinYenidenGrupla();
    } catch (err) {
      console.error('[KartSahibi] tolerans sonrasi yeniden gruplama hatasi', err);
    } finally {
      this.cd.markForCheck();
    }
  }

  /** Okunan giriş/çıkış saatini tolerans aralığı içindeyse hedef saate yuvarlar.
   *  manuel=true (elle girilen) kayıtlar asla yuvarlanmaz — gerçek saat korunur. */
  private _yuvalaTolerans(
    okunanIso: string,
    hedefSaat: string,
    manuel: boolean,
  ): { saat: string; yuvarlandi: boolean } {
    const okunan = this.saatFormat(okunanIso).slice(0, 5);
    if (manuel || !hedefSaat) return { saat: okunan, yuvarlandi: false };
    const okunanDk = this._saatDk(okunan);
    const hedefDk = this._saatDk(hedefSaat);
    if (okunanDk < 0 || hedefDk < 0) return { saat: okunan, yuvarlandi: false };
    if (Math.abs(hedefDk - okunanDk) <= this.toleransDk()) return { saat: hedefSaat, yuvarlandi: true };
    return { saat: okunan, yuvarlandi: false };
  }

  private _saatDk(hhmm: string): number {
    const [s, d] = (hhmm || '').split(':').map(Number);
    return isNaN(s) || isNaN(d) ? -1 : s * 60 + d;
  }

  /** Okunan ISO zamanına tolerans uygular; yuvarlama yapılırsa aynı gündeki hedef saatli
   *  yeni bir ISO döndürür. manuel kayıtlara dokunmaz. */
  private _toleransIsoUygula(
    okunanIso: string,
    hedefSaat: string,
    manuel: boolean,
  ): { zaman: string; yuvarlandi: boolean } | null {
    if (!okunanIso) return null;
    if (manuel || !hedefSaat) return { zaman: okunanIso, yuvarlandi: false };
    const r = this._yuvalaTolerans(okunanIso, hedefSaat, false);
    if (!r.yuvarlandi) return { zaman: okunanIso, yuvarlandi: false };
    // Yuvarlandi -> ayni gunun hedef saatine yeni tarih uret (gun kaymasini onle)
    const d = new Date(okunanIso);
    const [h, m] = r.saat.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return { zaman: okunanIso, yuvarlandi: false };
    d.setHours(h, m, 0, 0);
    return { zaman: d.toISOString(), yuvarlandi: true };
  }

  /** Çoklu giriş/çıkış modu: eşiklenmiş okutma gruplarını sırayla giriş/çıkış/giriş/çıkış...
   *  olarak eşleştirip segment listesi üretir. Tolerans SADECE ilk segmentin girişine ve
   *  (varsa) son KAPALI segmentin çıkışına uygulanır — aradaki giriş/çıkışlar (ör. öğlen
   *  izne çıkıp dönme) vardiya başlangıç/bitiş hedefine yuvarlanmaz, ham saatiyle kalır. */
  private _segmentleriOlustur(gunGruplari: IOkutmaGrubu[]): IGirisCikisSegment[] {
    const segmentler: IGirisCikisSegment[] = [];
    for (let i = 0; i < gunGruplari.length; i += 2) {
      const girisGrup = gunGruplari[i];
      const cikisGrup = gunGruplari[i + 1]; // undefined ise açık segment (henüz çıkış yok)

      const ilkSegmentMi = i === 0;
      const sonKapaliSegmentMi = !!cikisGrup && i + 2 >= gunGruplari.length;

      const girisTol = this._toleransIsoUygula(
        girisGrup.ilkOlay.zaman,
        ilkSegmentMi ? this.girisHedefSaat() : '',
        !!girisGrup.ilkOlay.manuel,
      );
      const cikisTol = cikisGrup
        ? this._toleransIsoUygula(
            cikisGrup.sonOlay.zaman,
            sonKapaliSegmentMi ? this.cikisHedefSaat() : '',
            !!cikisGrup.sonOlay.manuel,
          )
        : null;

      const girisZaman = girisTol?.zaman ?? girisGrup.ilkOlay.zaman;
      const cikisZaman = cikisTol?.zaman ?? '';

      segmentler.push({
        girisZaman,
        cikisZaman,
        giris: this.saatFormat(girisZaman).slice(0, 5),
        cikis: cikisZaman ? this.saatFormat(cikisZaman).slice(0, 5) : '',
        girisYuvari: !!girisTol?.yuvarlandi,
        cikisYuvari: !!cikisTol?.yuvarlandi,
        acikMi: !cikisGrup,
        sure: cikisZaman ? this.sureFarkiHesapla(girisZaman, cikisZaman) : '',
      });
    }
    return segmentler;
  }

  /** Segment listesindeki KAPALI (çıkışı olan) segmentlerin toplam süresini dakika olarak
   *  döner. Açık segment (henüz çıkış okutulmamış) ve segmentler arasındaki boşluklar
   *  (izin/mola çıkışı vb.) süreye dahil edilmez — böylece gün-içi izin süresi otomatik
   *  olarak dışarıda kalır. */
  private _segmentlerToplamDk(segmentler: IGirisCikisSegment[]): number {
    return segmentler.reduce((acc, s) => {
      if (!s.cikisZaman) return acc;
      const ilk = new Date(s.girisZaman).getTime();
      const son = new Date(s.cikisZaman).getTime();
      if (isNaN(ilk) || isNaN(son) || son < ilk) return acc;
      return acc + Math.floor((son - ilk) / 60000);
    }, 0);
  }

  /** Tolerans/ayar değişince aya ait tüm günleri yeniden gruplar (tolerans uygular). */
  private _ayIcinYenidenGrupla() {
    if (!this.kartUid) return;
    const t = this.seciliTarih();
    const ayBas = new Date(t.getFullYear(), t.getMonth(), 1);
    const aySon = new Date(t.getFullYear(), t.getMonth() + 1, 1);
    this.gunler.set(this.gunlereGrupla(this._hamOlaylar, ayBas, aySon));
    const acikGun = this.dialogGun();
    if (acikGun) {
      const guncel = this.gunler().find(g => g.tarih === acikGun.tarih);
      this.dialogGun.set(guncel || null);
    }
    this.cd.markForCheck();
  }

  /** Her okutmayı bağımsız bir vuruş kabul ederek, arası < eşik olan ardışık
   *  okutmaları tek bir IOkutmaGrubu'nda toplar. Grubun ilk okutması giriş,
   *  son okutması çıkış saati olarak kullanılır. Durum (koyuldu/cekildi) dikkate alınmaz. */
  okutmaGruplari(olaylar: IKartSahibiOlay[]): IOkutmaGrubu[] {
    if (!this.grupOkutmaAcik() || this.grupEsikSaniye() <= 0 || olaylar.length === 0) {
      return olaylar.map(o => ({ ilkOlay: o, sonOlay: o, tumOlaylar: [o], grupMu: false }));
    }

    const esikMs = this.grupEsikSaniye() * 1000;
    const gruplar: IOkutmaGrubu[] = [];
    let mevcutGrup: IKartSahibiOlay[] = [olaylar[0]];

    for (let i = 1; i < olaylar.length; i++) {
      const onceki = mevcutGrup[mevcutGrup.length - 1];
      const simdi = olaylar[i];
      const fark = new Date(simdi.zaman).getTime() - new Date(onceki.zaman).getTime();
      if (fark <= esikMs) {
        mevcutGrup.push(simdi);
      } else {
        gruplar.push(this._grubuOlustur(mevcutGrup));
        mevcutGrup = [simdi];
      }
    }
    if (mevcutGrup.length) gruplar.push(this._grubuOlustur(mevcutGrup));
    return gruplar;
  }

  private _grubuOlustur(olaylar: IKartSahibiOlay[]): IOkutmaGrubu {
    return {
      ilkOlay: olaylar[0],
      sonOlay: olaylar[olaylar.length - 1],
      tumOlaylar: olaylar,
      grupMu: olaylar.length > 1,
    };
  }

  /** Grup düzenleme — ilk ve/veya son okutmanın saatini düzenle */
  grupDuzenleAc(grup: IOkutmaGrubu) {
    const ilk = grup.ilkOlay;
    const son = grup.sonOlay;
    if (!ilk._id) return;
    if (!ilk.manuel && !son.manuel && !this.superYoneticiMi) return;
    const d = new Date(ilk.zaman);
    this.duzenleGrupTarih.set(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`);
    this.duzenleGrupIlkSaat.set(`${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`);
    const ds = new Date(son.zaman);
    this.duzenleGrupSonSaat.set(`${String(ds.getHours()).padStart(2,'0')}:${String(ds.getMinutes()).padStart(2,'0')}`);
    this.duzenlenenGrupIlkId.set(ilk._id || null);
    this.duzenlenenGrupSonId.set(son._id || null);
    this.cd.markForCheck();
  }

  grupDuzenleKapat() {
    this.duzenlenenGrupIlkId.set(null);
    this.duzenlenenGrupSonId.set(null);
    this.cd.markForCheck();
  }

  async grupDuzenleKaydet(grup: IOkutmaGrubu) {
    if (this.duzenleGrupKaydediliyor()) return;
    this.duzenleGrupKaydediliyor.set(true);
    this.cd.markForCheck();
    try {
      const tarih = this.duzenleGrupTarih();
      const ilkIso = new Date(`${tarih}T${this.duzenleGrupIlkSaat()}:00`).toISOString();
      const sonIso = new Date(`${tarih}T${this.duzenleGrupSonSaat()}:00`).toISOString();

      const guncellemeler: Promise<any>[] = [];

      // İlk okutma güncelle
      if (grup.ilkOlay._id && (grup.ilkOlay.manuel || this.superYoneticiMi)) {
        const ist = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.pdks_kart_hareketler.guncelle', gecikmeliCevap: false });
        ist.parametreler = {
          filter: { _id: grup.ilkOlay._id, vt: 'FikoYT.sistem.cihaz.olaylar' },
          set: { sunucu_zamani: ilkIso },
        };
        guncellemeler.push(ist.isteSync());
      }

      // Son okutma — tekil grup ise aynı kayıt, güncelleme yapma (zaten ilk ile aynı)
      if (grup.grupMu && grup.sonOlay._id && grup.sonOlay._id !== grup.ilkOlay._id && (grup.sonOlay.manuel || this.superYoneticiMi)) {
        const ist2 = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.pdks_kart_hareketler.guncelle', gecikmeliCevap: false });
        ist2.parametreler = {
          filter: { _id: grup.sonOlay._id, vt: 'FikoYT.sistem.cihaz.olaylar' },
          set: { sunucu_zamani: sonIso },
        };
        guncellemeler.push(ist2.isteSync());
      }

      await Promise.all(guncellemeler);
      this.duzenlenenGrupIlkId.set(null);
      this.duzenlenenGrupSonId.set(null);
      await this.ayVerisiYukle();
    } catch (err) {
      console.error('[KartSahibi] grup düzenlenemedi', err);
    } finally {
      this.duzenleGrupKaydediliyor.set(false);
      this.cd.markForCheck();
    }
  }

  /** Grubu tamamen sil (tüm alt okutmalar) */
  async grupSil(grup: IOkutmaGrubu) {
    const hedefler = grup.tumOlaylar;
    if (!hedefler.every(o => o.manuel) && !this.superYoneticiMi) return;
    try {
      for (const o of hedefler) {
        if (!o._id) continue;
        const ist = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.pdks_kart_hareketler.sil', gecikmeliCevap: false });
        ist.parametreler = { _id: o._id, vt: 'FikoYT.sistem.cihaz.olaylar' };
        await ist.isteSync();
      }
      await this.ayVerisiYukle();
    } catch (err) {
      console.error('[KartSahibi] grup silinemedi', err);
    }
  }

  // ─── Manuel kayıt ─────────────────────────────────────────────

  manuelFormAc(g: IKartSahibiGun) {
    this.manuelTarih.set(g.tarih);
    this.manuelSaat.set(new Date().toTimeString().slice(0, 5));
    this.manuelDurum.set('koyuldu');
    this.manuelFormAcik.set(true);
    this.cd.markForCheck();
  }

  manuelFormKapat() {
    this.manuelFormAcik.set(false);
    this.cd.markForCheck();
  }

  async manuelKaydet() {
    const uid = this.kartUid;
    if (!uid || !this.manuelTarih() || !this.manuelSaat() || this.manuelKaydediliyor()) return;

    this.manuelKaydediliyor.set(true);
    this.cd.markForCheck();
    try {
      const isoZaman = new Date(`${this.manuelTarih()}T${this.manuelSaat()}:00`).toISOString();
      const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.pdks_kart_hareketler.ekle', gecikmeliCevap: false });
      istek.parametreler = {
        vt: 'FikoYT.sistem.cihaz.olaylar',
        durum: this.manuelDurum(),
        kart_kimlik: uid,
        sunucu_zamani: isoZaman,
        manuel: true,
      };
      await istek.isteSync();
      this.manuelFormAcik.set(false);
      await this.ayVerisiYukle();
    } catch (err) {
      console.error('[KartSahibi] manuel kayıt eklenemedi', err);
    } finally {
      this.manuelKaydediliyor.set(false);
      this.cd.markForCheck();
    }
  }

  /** "Normal çalışıldı" kaydediliyor mu — buton devre dışı bırakmak için. */
  normalCalismaKaydediliyor = signal(false);

  /** Kart okutması olmayan (veya eksik) bir gün için tek tıkla "normal çalışıldı" günü
   *  oluşturur: Ayarlar'daki giriş/çıkış hedef saatlerini iki manuel kayıt olarak ekler,
   *  ve henüz eklenmemiş tüm hızlı mola şablonlarını (Sabah çayı, Öğle yemeği, Akşam çayı)
   *  otomatik ekler. Hedef saatler boşsa (Ayarlar'dan girilmemişse) işlem yapılmaz. */
  async normalCalismaEkle(gun: IKartSahibiGun) {
    const uid = this.kartUid;
    const giris = this.girisHedefSaat();
    const cikis = this.cikisHedefSaat();
    
    if (!uid || this.normalCalismaKaydediliyor()) return;

    if (!giris || !cikis) {
      alert('Hata: Hedef giriş/çıkış saatleri ayarlar kısmında kayıtlı değil. Lütfen önce saatleri tanımlayınız.');
      return;
    }

    this.normalCalismaKaydediliyor.set(true);
    this.cd.markForCheck();
    try {
      const girisIso = new Date(`${gun.tarih}T${giris}:00`).toISOString();
      const cikisIso = new Date(`${gun.tarih}T${cikis}:00`).toISOString();

      const girisIstek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.pdks_kart_hareketler.ekle', gecikmeliCevap: false });
      girisIstek.parametreler = { vt: 'FikoYT.sistem.cihaz.olaylar', durum: 'koyuldu', kart_kimlik: uid, sunucu_zamani: girisIso, manuel: true };
      await girisIstek.isteSync();

      const cikisIstek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.pdks_kart_hareketler.ekle', gecikmeliCevap: false });
      cikisIstek.parametreler = { vt: 'FikoYT.sistem.cihaz.olaylar', durum: 'cekildi', kart_kimlik: uid, sunucu_zamani: cikisIso, manuel: true };
      await cikisIstek.isteSync();

      // Hızlı mola şablonları — bu güne henüz eklenmemiş olanları ekle
      for (const s of this.kesintiSablonlari) {
        const zatenVar = gun.kesintiler?.some(k => k.tur === s.tur && k.baslangic === s.baslangic && k.bitis === s.bitis);
        if (zatenVar) continue;
        const molaIstek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.pdks_gun_kesintiler.ekle', gecikmeliCevap: false });
        molaIstek.parametreler = { kart_kimlik: uid, tarih: gun.tarih, tur: s.tur, baslangic: s.baslangic, bitis: s.bitis, aciklama: s.aciklama || '' };
        await molaIstek.isteSync();
      }

      await this.ayVerisiYukle();
    } catch (err) {
      console.error('[KartSahibi] normal çalışma eklenemedi', err);
    } finally {
      this.normalCalismaKaydediliyor.set(false);
      this.cd.markForCheck();
    }
  }

  /** Bir güne ait TÜM hareketleri silme sırasında true olur (buton devre dışı bırakmak için). */
  gunSilKaydediliyor = signal(false);

  /** Bu gün silinebilir mi? — kayıt yoksa hayır; günde en az bir cihaz (manuel olmayan)
   *  kaydı varsa sadece süper yönetici silebilir, tamamı manuelse herkes silebilir. */
  gunSilinebilirMi(g: IKartSahibiGun): boolean {
    return g.sayisi > 0 && (g.olaylar.every(o => o.manuel) || this.superYoneticiMi);
  }

  /** Seçili güne ait TÜM ham hareket kayıtlarını (koyuldu/cekildi) tek seferde siler.
   *  Yetki kuralı olaySil/grupSil ile aynıdır: manuel kayıtlar herkes, cihaz kayıtları
   *  sadece süper yönetici tarafından silinebilir — günde tek bir cihaz kaydı bile olsa
   *  ve süper yönetici değilse işlem tümüyle engellenir (kısmi silme yapılmaz). */
  async gunuSil(g: IKartSahibiGun) {
    if (!g || this.gunSilKaydediliyor() || !this.gunSilinebilirMi(g)) return;
    this.gunSilKaydediliyor.set(true);
    this.cd.markForCheck();
    try {
      for (const o of g.olaylar) {
        if (!o._id) continue;
        const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.pdks_kart_hareketler.sil', gecikmeliCevap: false });
        // olaySil()'deki NOT ile aynı gerekçe: filtre alanları düz gönderiliyor.
        istek.parametreler = { _id: o._id, vt: 'FikoYT.sistem.cihaz.olaylar' };
        await istek.isteSync();
      }
      this.dialogKapat();
      await this.ayVerisiYukle();
    } catch (err) {
      console.error('[KartSahibi] gün silinemedi', err);
    } finally {
      this.gunSilKaydediliyor.set(false);
      this.cd.markForCheck();
    }
  }

  /** Manuel kayıtlar herkes tarafından, cihazdan gelen (gerçek okuma) kayıtlar
   *  ise sadece süper yönetici tarafından silinebilir. Gruplanmış satırda (grupOlaylari)
   *  altındaki tüm ham kayıtlar tek tek silinir. */
  async olaySil(olay: IKartSahibiOlay) {
    const hedefler = olay.grupOlaylari && olay.grupOlaylari.length > 1 ? olay.grupOlaylari : [olay];
    if (!hedefler.every((o) => o.manuel) && !this.superYoneticiMi) return;
    try {
      for (const o of hedefler) {
        if (!o._id) continue;
        const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.pdks_kart_hareketler.sil', gecikmeliCevap: false });
        // NOT: backend'in genel DB silme route'u (kokDB fikoyt_sistem_db_sil) gönderilen
        // JSON body'nin TAMAMINI mongo filtresi olarak kullanıyor (silFiltre = Istek.Json) —
        // '{filter: {...}}' gibi bir sarmalama gönderilirse hiçbir belge eşleşmez ve silme
        // sessizce hiçbir şey yapmaz (bug buydu). Bu yüzden filtre alanları düz gönderiliyor.
        istek.parametreler = { _id: o._id, vt: 'FikoYT.sistem.cihaz.olaylar' };
        await istek.isteSync();
      }
      await this.ayVerisiYukle();
    } catch (err) {
      console.error('[KartSahibi] kayıt silinemedi', err);
    }
  }

  // ─── Kayıt düzenleme ──────────────────────────────────────────
  // Silme ile aynı yetki kuralı: manuel kayıtlar herkes tarafından,
  // cihazdan gelen kayıtlar ise sadece süper yönetici tarafından düzenlenebilir.

  duzenleAc(olay: IKartSahibiOlay) {
    if (!olay._id) return;
    if (!olay.manuel && !this.superYoneticiMi) return;
    const d = new Date(olay.zaman);
    if (isNaN(d.getTime())) return;
    this.duzenleTarih.set(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    this.duzenleSaat.set(`${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`);
    this.duzenleDurum.set(olay.durum);
    this.duzenlenenOlayId.set(olay._id);
    this.cd.markForCheck();
  }

  duzenleKapat() {
    this.duzenlenenOlayId.set(null);
    this.cd.markForCheck();
  }

  async duzenleKaydet(olay: IKartSahibiOlay) {
    if (!olay._id || !this.duzenleTarih() || !this.duzenleSaat() || this.duzenleKaydediliyor()) return;

    this.duzenleKaydediliyor.set(true);
    this.cd.markForCheck();
    try {
      const isoZaman = new Date(`${this.duzenleTarih()}T${this.duzenleSaat()}:00`).toISOString();
      const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.pdks_kart_hareketler.guncelle', gecikmeliCevap: false });
      istek.parametreler = {
        filter: { _id: olay._id, vt: 'FikoYT.sistem.cihaz.olaylar' },
        set: { durum: this.duzenleDurum(), sunucu_zamani: isoZaman },
      };
      await istek.isteSync();
      this.duzenlenenOlayId.set(null);
      await this.ayVerisiYukle();
    } catch (err) {
      console.error('[KartSahibi] kayıt güncellenemedi', err);
    } finally {
      this.duzenleKaydediliyor.set(false);
      this.cd.markForCheck();
    }
  }

  // ─── Veri yükleme + eşleştirme ────────────────────────────────

  private async ayVerisiYukle() {
    const uid = this.kartUid;
    if (!uid) { this.gunler.set([]); return; }

    // Bu metod TÜM hareket değişikliklerinin (manuel ekleme, düzenleme, silme,
    // grup düzenleme/silme) sonunda çağrılır — buradan `pdks_kart_hareketler`
    // koleksiyonunu canlı okuyan bileşenlere (firmada-panel vb.) sinyal yolla ki
    // onlar da anında yeniden hesaplayabilsin.
    try { this.wsService.hareketDegisti$.next(); } catch { /* sinyal zorunlu değil */ }

    const t = this.seciliTarih();
    const bas = new Date(t.getFullYear(), t.getMonth(), 1);
    const son = new Date(t.getFullYear(), t.getMonth() + 1, 1);
    this.gunleriYukle(bas, son);
  }

  /** Verilen tarih aralığındaki (ay) okuma kayıtlarını meta + kesintiyle paralel çekip gün gruplarına böler. */
  private async gunleriYukle(bas: Date, son: Date) {
    const uid = this.kartUid;
    if (!uid) { this.gunler.set([]); return; }

    this.yukleniyor.set(true);
    this.cd.markForCheck();

    try {
      const iso = (x: Date) => x.toISOString();
      const basTarih = `${bas.getFullYear()}-${String(bas.getMonth() + 1).padStart(2, '0')}-01`;
      const sonTarih = `${son.getFullYear()}-${String(son.getMonth() + 1).padStart(2, '0')}-01`;

      // Hareket verisi + gün meta verisi paralel çek
      const hareketIstek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.pdks_kart_hareketler.aggregate' });
      hareketIstek.parametreler = {
        aggregate: [
          { $match: {
              vt: 'FikoYT.sistem.cihaz.olaylar',
              durum: { $in: ['koyuldu', 'cekildi'] },
              kart_kimlik: uid,
              sunucu_zamani: { $gte: iso(bas), $lt: iso(son) },
          } },
          // NOT: Bu $sort sadece ilk yüklemeyi hızlandırır — kesin sıralama garantisi
          // olarak KULLANILMAZ. Canlı WS güncellemesi, manuel kayıt ekleme/düzenleme
          // sonrası bu sorgu tekrar çalıştırılır ve sonuç `gunlereGrupla()` içinde HER
          // SEFERİNDE sayısal zaman damgasına (getTime()) göre yeniden sıralanır —
          // bkz. gunlereGrupla() başındaki `hamSirali`.
          { $sort: { sunucu_zamani: 1 } },
          { $project: { _id: 1, zaman: '$sunucu_zamani', durum: 1, manuel: 1 } },
        ],
      };

      const metaIstek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.pdks_gun_meta.aggregate' });
      metaIstek.parametreler = {
        aggregate: [
          { $match: { kart_kimlik: uid, tarih: { $gte: basTarih, $lt: sonTarih } } },
        ],
      };

      const kesintiIstek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.pdks_gun_kesintiler.aggregate' });
      kesintiIstek.parametreler = {
        aggregate: [
          { $match: { kart_kimlik: uid, tarih: { $gte: basTarih, $lt: sonTarih } } },
          { $sort: { tarih: 1 } },
        ],
      };

      await Promise.all([hareketIstek.isteSync(), metaIstek.isteSync(), kesintiIstek.isteSync()]);

      const ham: any[] = hareketIstek.cevap?.sonuc || (Array.isArray(hareketIstek.cevap) ? hareketIstek.cevap : []) || [];
      this._hamOlaylar = ham;
      const metaHam: any[] = metaIstek.cevap?.sonuc || (Array.isArray(metaIstek.cevap) ? metaIstek.cevap : []) || [];
      const kesintiHam: any[] = kesintiIstek.cevap?.sonuc || (Array.isArray(kesintiIstek.cevap) ? kesintiIstek.cevap : []) || [];

      // Meta map'i güncelle
      this._gunMetaMap.clear();
      for (const m of metaHam) {
        if (m?.tarih) this._gunMetaMap.set(m.tarih, { _id: m._id, kart_kimlik: m.kart_kimlik, tarih: m.tarih, tatilMi: !!m.tatilMi, aciklama: m.aciklama || '' });
      }

      // Kesinti map'i güncelle — tarih başına birden fazla kesinti olabilir
      this._gunKesintiMap.clear();
      for (const k of kesintiHam) {
        if (!k?.tarih) continue;
        if (!this._gunKesintiMap.has(k.tarih)) this._gunKesintiMap.set(k.tarih, []);
        this._gunKesintiMap.get(k.tarih)!.push({
          _id: k._id, kart_kimlik: k.kart_kimlik, tarih: k.tarih, tur: k.tur, baslangic: k.baslangic, bitis: k.bitis, aciklama: k.aciklama || '',
        });
      }

      this.gunler.set(this.gunlereGrupla(ham, bas, son));

      // Dialog açıksa (canlı izleniyorsa) içeriğini de güncel günün taze veriyle değiştir
      const acikGun = this.dialogGun();
      if (acikGun) {
        const guncel = this.gunler().find(g => g.tarih === acikGun.tarih);
        this.dialogGun.set(guncel || null);
      }
    } catch (err) {
      console.error('[KartSahibi] ay verisi yüklenemedi', err);
      this.gunler.set([]);
    } finally {
      this.yukleniyor.set(false);
      this.cd.markForCheck();
    }
  }

  /**
   * Ham (zaman sıralı) koyuldu/cekildi olaylarını GÜN GRUPLAMASINDAN ÖNCE eşleştirir:
   * ardışık koyuldu→cekildi çiftleri gece yarısı sınırı göz ardı edilerek tek bir
   * "okuma" sayılır ve GÜN ataması koyuldu'nun tarihine göre yapılır (yani 23:58
   * giriş + 00:03 çıkış aynı — girişin olduğu — güne yazılır, ikiye bölünmez).
   * Eşsiz (çifti bulunamayan) koyuldu/cekildi kendi tarihinde tek başına (fallback)
   * gösterilir: örn. ay ortasında biten bir giriş (henüz çıkış okutulmamış) ya da
   * ayın başında rastlanan, önceki aydan kalma bir çıkış.
   * Son olarak, kayıt bulunmayan günler için de "Hareket yok" gösterilebilsin diye
   * boş satırlar üretilir (geçmiş aylarda ayın tamamı, bu ayda bugüne kadar).
   */
  private gunlereGrupla(ham: any[], ayBas: Date, aySon: Date): IKartSahibiGun[] {
    // KRİTİK: DB'den $sort:{sunucu_zamani:1} ile sıralı geldiği varsayılsa da, bu diziye
    // sonradan (canlı WS güncellemesi, manuel kayıt ekleme/düzenleme sonrası ayVerisiYukle
    // yeniden çağrısı) eklenen kayıtların gerçekten kronolojik sırada olduğuna asla
    // güvenilmez. koyuldu/cekildi eşleştirme mantığı SIRAYA bağlı olduğundan, işlemeye
    // başlamadan önce SAYISAL zaman damgasına göre (ISO string karşılaştırması DEĞİL —
    // milisaniye/format farkları yanlış sıra verebilir) tek seferlik ve kesin bir sıralama
    // uygulanır.
    const hamSirali = [...(ham || [])].sort((a, b) => new Date(a.zaman).getTime() - new Date(b.zaman).getTime());
    const gunOlaylari = new Map<string, IKartSahibiOlay[]>();
    const ekle = (tarih: string, olay: IKartSahibiOlay) => {
      if (!gunOlaylari.has(tarih)) gunOlaylari.set(tarih, []);
      gunOlaylari.get(tarih)!.push(olay);
    };
    const olayaCevir = (o: any, durum: 'koyuldu' | 'cekildi'): IKartSahibiOlay =>
      ({ _id: o._id, zaman: o.zaman, durum, manuel: !!o.manuel });
    // ÖNEMLİ: sunucu_zamani UTC ISO olarak saklanıyor. String(...).slice(0,10) UTC
    // takvim gününü verir — UTC+3'te 00:00–02:59 arası yerel kayıtlar bir önceki
    // UTC gününe denk düşüp YANLIŞ (bir önceki) güne yazılıyordu. Bunun yerine
    // zaman damgasını yerel tarihe çevirip ona göre grupluyoruz.
    const yerelTarih = (iso: string) => {
      const d = new Date(iso);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    let bekleyenKoyuldu: any = null;
    for (const o of hamSirali) {
      if (o.durum === 'koyuldu') {
        if (bekleyenKoyuldu) {
          // önceki koyuldu hiç çıkışla eşleşmeden yeni bir koyuldu geldi → fallback: kendi gününde tek başına
          ekle(yerelTarih(bekleyenKoyuldu.zaman), olayaCevir(bekleyenKoyuldu, 'koyuldu'));
        }
        bekleyenKoyuldu = o;
      } else {
        if (bekleyenKoyuldu) {
          // eşleşen çift → GÜN ataması koyuldu tarihine göre (gece yarısı bölünmez)
          const gunTarihi = yerelTarih(bekleyenKoyuldu.zaman);
          ekle(gunTarihi, olayaCevir(bekleyenKoyuldu, 'koyuldu'));
          ekle(gunTarihi, olayaCevir(o, 'cekildi'));
          bekleyenKoyuldu = null;
        } else {
          // eşsiz çıkış (ör. önceki aydan/görünmeyen bir girişin devamı) → fallback: kendi gününde
          ekle(yerelTarih(o.zaman), olayaCevir(o, 'cekildi'));
        }
      }
    }
    if (bekleyenKoyuldu) {
      // ay sonunda hâlâ "içeride" (çıkışı henüz okutulmamış) → fallback: kendi gününde tek başına
      ekle(yerelTarih(bekleyenKoyuldu.zaman), olayaCevir(bekleyenKoyuldu, 'koyuldu'));
    }

    // Ayın günleri için satır üret (kayıt olmasa da "Hareket yok" gösterilsin) —
    // geçmiş aylarda ayın tamamı, bu ayda bugüne kadar.
    const bugun = new Date();
    const bugunTarihStr = `${bugun.getFullYear()}-${String(bugun.getMonth() + 1).padStart(2, '0')}-${String(bugun.getDate()).padStart(2, '0')}`;
    const ayGuncelMi = ayBas.getFullYear() === bugun.getFullYear() && ayBas.getMonth() === bugun.getMonth();
    const sonGunTarihi = new Date(aySon.getTime() - 1);
    const gosterilecekSon = ayGuncelMi ? bugun.getDate() : sonGunTarihi.getDate();

    const gunler: IKartSahibiGun[] = [];
    for (let d = new Date(ayBas); d.getTime() <= aySon.getTime(); d.setDate(d.getDate() + 1)) {
      const tarih = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      // KRİTİK: ISO string karşılaştırması (localeCompare) DEĞİL — cihaz kayıtları ile
      // manuel eklenen/düzenlenen kayıtların ISO gösterimi (milisaniye hassasiyeti,
      // Z / +00:00 farkı vb.) birebir aynı olmayabilir; bu, string sıralamasını yanlış
      // sıraya sokabilir. Sayısal zaman damgası (getTime()) her zaman doğru kronolojik
      // sırayı verir ve dizinin DB'ye/diziye ekleniş sırasından tamamen bağımsızdır.
      const olaylar = (gunOlaylari.get(tarih) || []).sort((a, b) => new Date(a.zaman).getTime() - new Date(b.zaman).getTime());
      const haftaGunu = d.getDay(); // 0=Pazar, 6=Cumartesi
      // Gruplama açıksa: Günün ilk grubu giriş, son grubu çıkış olarak gösterilir.
      // Kapalıysa: tüm okutmaların ilki ve sonuncusu kullanılır (mevcut davranış).
      // Çoklu giriş/çıkış modu açıksa: eşiklenmiş gruplar sırayla giriş/çıkış/giriş/çıkış...
      // sayılır (bkz. _segmentleriOlustur) — gün içinde birden fazla mesai bloğu olabilir.
      const gunGruplari = this.okutmaGruplari(olaylar);
      let ilkKesin = '', sonKesin = '';
      let ilkYuvari = false, sonYuvari = false;
      let segmentler: IGirisCikisSegment[] = [];
      let toplamSureDk: number | null = null;

      if (this.cokluGirisCikisAcik()) {
        segmentler = this._segmentleriOlustur(gunGruplari);
        const ilkSeg = segmentler[0];
        const sonKapaliSeg = [...segmentler].reverse().find(s => !s.acikMi);
        ilkKesin = ilkSeg?.girisZaman ?? '';
        sonKesin = sonKapaliSeg?.cikisZaman ?? '';
        ilkYuvari = !!ilkSeg?.girisYuvari;
        sonYuvari = !!sonKapaliSeg?.cikisYuvari;
        toplamSureDk = segmentler.length ? this._segmentlerToplamDk(segmentler) : null;
      } else {
        const ilkGrup = gunGruplari[0];
        const sonGrup = gunGruplari[gunGruplari.length - 1];
        const ilkZaman = ilkGrup ? ilkGrup.ilkOlay.zaman : '';
        const sonZaman = sonGrup ? sonGrup.sonOlay.zaman : '';
        // Tolance: only non-manual (okunan) giris/cikis rounded to target. Manual kayitlar never touched.
        const ilkTol = ilkZaman ? this._toleransIsoUygula(ilkZaman, this.girisHedefSaat(), !!ilkGrup?.ilkOlay.manuel) : null;
        const sonTol = sonZaman ? this._toleransIsoUygula(sonZaman, this.cikisHedefSaat(), !!sonGrup?.sonOlay.manuel) : null;
        ilkKesin = ilkTol?.zaman ?? '';
        sonKesin = sonTol?.zaman ?? '';
        ilkYuvari = !!ilkTol?.yuvarlandi;
        sonYuvari = !!sonTol?.yuvarlandi;
      }
      const gunKesintileri = this._gunKesintiMap.get(tarih) || [];

      // Çoklu modda ham/net süre segmentlerin TOPLAMI (aradaki izin boşluğu hariç);
      // tekli modda eskisi gibi ilk-son farkı.
      const sureStr = toplamSureDk !== null
        ? (toplamSureDk > 0 ? this.dakikaFormat(toplamSureDk) + ':00' : '')
        : (ilkKesin && sonKesin && ilkKesin !== sonKesin ? this.sureFarkiHesapla(ilkKesin, sonKesin) : '');
      const netSureStr = toplamSureDk !== null
        ? (toplamSureDk > 0 ? this.dakikaFormat(toplamSureDk - this.molaToplamDk(gunKesintileri)) : '')
        : (ilkKesin && sonKesin && ilkKesin !== sonKesin ? this.netSureHesapla(ilkKesin, sonKesin, gunKesintileri) : '');

      gunler.push({
        tarih,
        gun: d.getDate(),
        gunAdi: GUN_ISIMLERI_KISA[haftaGunu],
        bugunMu: tarih === bugunTarihStr,
        ilk: ilkKesin ? this.saatFormat(ilkKesin).slice(0, 5) : '',
        son: sonKesin ? this.saatFormat(sonKesin).slice(0, 5) : '',
        ilkYuvari,
        sonYuvari,
        sure: sureStr,
        sayisi: olaylar.length,
        olaylar,
        haftaSonuMu: haftaGunu === 0 || haftaGunu === 6,
        tatilMi: this.tatilGunuMu(tarih),
        aciklama: this._gunMetaMap.get(tarih)?.aciklama ?? '',
        metaId: this._gunMetaMap.get(tarih)?._id,
        kesintiler: gunKesintileri,
        netSure: netSureStr,
        segmentler,
      });
    }
    // Bugundan sonraki satirlari at — hafta/ay gorumunu buguna kadar gosterir.
    return gunler.filter(g => new Date(`${g.tarih}T00:00:00`).getTime() <= bugun.getTime() + 24 * 60 * 60 * 1000);
  }

  private tatilGunuMu(tarih: string): boolean {
    return !!this._gunMetaMap.get(tarih)?.tatilMi;
  }

  /**
   * pdks_gun_meta kaydını kalıcı hale getirir.
   * Sunucuda 'upsert' komutu bulunmadığı için: mevcut kayıt varsa 'guncelle',
   * yoksa 'ekle' komutu kullanılır (proje deseni — bkz. kesinti ekle/guncelle).
   * Dönen değer: yeni eklenen kaydın _id'si (güncellemede mevcut _id).
   */
  private async _metaKaydet(tarih: string, alanlar: { tatilMi?: boolean; aciklama?: string }): Promise<string | undefined> {
    const mevcut = this._gunMetaMap.get(tarih);
    const tamBelge = {
      kart_kimlik: this.kartUid,
      tarih,
      tatilMi: alanlar.tatilMi !== undefined ? alanlar.tatilMi : !!mevcut?.tatilMi,
      aciklama: alanlar.aciklama !== undefined ? alanlar.aciklama : (mevcut?.aciklama ?? ''),
    };
    if (mevcut?._id) {
      const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.pdks_gun_meta.guncelle', gecikmeliCevap: false });
      istek.parametreler = { filter: { _id: mevcut._id }, set: { tatilMi: tamBelge.tatilMi, aciklama: tamBelge.aciklama } };
      await istek.isteSync();
      return mevcut._id;
    }
    const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.pdks_gun_meta.ekle', gecikmeliCevap: false });
    istek.parametreler = tamBelge;
    await istek.isteSync();
    // Sunucu cevabı farklı biçimlerde gelebilir: sonuc bir dizi içinde { UpsertedID } olabilir
    const ham: any = istek.cevap?.sonuc;
    const sonuc: any = Array.isArray(ham) ? ham[0] : ham;
    const gelenId = sonuc?._id ?? sonuc?.UpsertedID ?? sonuc?.upsertedId ?? sonuc?.insertedId ?? undefined;
    return (typeof gelenId === 'string' && gelenId ? gelenId : undefined);
  }

  /**
   * Tatil işaretle / kaldır.
   * pdks_gun_meta koleksiyonunda { kart_kimlik, tarih, tatilMi } belgesi tutulur.
   * — Kart olmasa da (kartUid boş) çalışır: genel tatil günü tanımlamak için.
   * — Kayıt yoksa ekle, varsa güncelle (_metaKaydet).
   * — UI anında güncellenir (optimistic), hata durumunda geri alınır.
   */
  async tatilToggle(tarih: string) {
    if (this.tatilKaydediliyor()) return;

    const mevcutMeta = this._gunMetaMap.get(tarih);
    const yeniDeger = !mevcutMeta?.tatilMi;

    // Optimistic UI güncelle
    this._gunMetaMapGuncelle(tarih, yeniDeger, mevcutMeta?._id);
    this._gunlerTatilGuncelle(tarih, yeniDeger);

    this.tatilKaydediliyor.set(true);
    this.cd.markForCheck();
    try {
      const gelenId = await this._metaKaydet(tarih, { tatilMi: yeniDeger });
      if (gelenId) {
        this._gunMetaMapGuncelle(tarih, yeniDeger, gelenId);
        this._gunlerTatilGuncelle(tarih, yeniDeger, gelenId);
      }
    } catch (err) {
      console.error('[KartSahibi] tatil meta kaydedilemedi', err);
      // Hata: geri al
      this._gunMetaMapGuncelle(tarih, !yeniDeger, mevcutMeta?._id);
      this._gunlerTatilGuncelle(tarih, !yeniDeger, mevcutMeta?._id);
    } finally {
      this.tatilKaydediliyor.set(false);
      this.cd.markForCheck();
    }
  }

  private _gunMetaMapGuncelle(tarih: string, tatilMi: boolean, id?: string, aciklama?: string) {
    const mevcut = this._gunMetaMap.get(tarih) ?? { kart_kimlik: this.kartUid, tarih, tatilMi: false };
    this._gunMetaMap.set(tarih, {
      ...mevcut,
      tatilMi,
      _id: id ?? mevcut._id,
      aciklama: aciklama !== undefined ? aciklama : mevcut.aciklama,
    });
  }

  private _gunlerTatilGuncelle(tarih: string, tatilMi: boolean, metaId?: string) {
    this.gunler.set(this.gunler().map(g =>
      g.tarih === tarih ? { ...g, tatilMi, metaId: metaId ?? g.metaId } : g
    ));
    const acik = this.dialogGun();
    if (acik?.tarih === tarih) {
      this.dialogGun.set({ ...acik, tatilMi, metaId: metaId ?? acik.metaId });
    }
  }

  /**
   * Açıklama blur'da çağrılır — değişmediyse istek atmaz.
   * _metaKaydet ile ekle/guncelle deseniyle kalıcı hale getirilir.
   */
  async aciklamaKaydet(tarih: string) {
    const yeniAciklama = this.aciklamaDraft().trim();
    const mevcut = this._gunMetaMap.get(tarih);
    if ((mevcut?.aciklama ?? '') === yeniAciklama) return; // değişmedi
    if (this.aciklamaKaydediliyor()) return;

    this.aciklamaKaydediliyor.set(true);
    this.cd.markForCheck();
    try {
      const gelenId = await this._metaKaydet(tarih, { aciklama: yeniAciklama });
      const mevcutTatil = !!mevcut?.tatilMi;
      this._gunMetaMapGuncelle(tarih, mevcutTatil, gelenId ?? mevcut?._id, yeniAciklama);
      // Gün listesini ve dialogu güncelle
      this.gunler.set(this.gunler().map(g =>
        g.tarih === tarih ? { ...g, aciklama: yeniAciklama } : g
      ));
      const acik = this.dialogGun();
      if (acik?.tarih === tarih) {
        this.dialogGun.set({ ...acik, aciklama: yeniAciklama });
      }
    } catch (err) {
      console.error('[KartSahibi] açıklama kaydedilemedi', err);
    } finally {
      this.aciklamaKaydediliyor.set(false);
      this.cd.markForCheck();
    }
  }

  // ─── Mola / İzin (kesinti) yönetimi ────────────────────────────
  // pdks_gun_kesintiler koleksiyonu: { kart_kimlik, tarih, tur, baslangic, bitis }.
  // manuelKaydet()/duzenleKaydet() ile aynı FikoYTIstekKok deseni kullanılır.

  /** Yeni kesinti formunu açar (ekleme modu). */
  kesintiFormAc() {
    this.kesintiTur.set('mola');
    this.kesintiBaslangic.set('');
    this.kesintiBitis.set('');
    this.kesintiAciklama.set('');
    this.duzenlenenKesintiId.set(null);
    this.kesintiFormAcik.set(true);
    this.cd.markForCheck();
  }

  kesintiFormKapat() {
    this.kesintiFormAcik.set(false);
    this.duzenlenenKesintiId.set(null);
    this.cd.markForCheck();
  }

  /** Bir şablon (ör. varsayılan öğle molası) bu güne zaten eklenmiş mi? */
  kesintiSablonMevcut(s: IKesintiSablon): boolean {
    const gun = this.dialogGun();
    return !!gun?.kesintiler?.some(k => k.tur === s.tur && k.baslangic === s.baslangic && k.bitis === s.bitis);
  }

  /** Bir şablonu (örn. öğle molası) form açmadan tek tıkla açar: ekli değilse ekler, ekliyse siler (toggle). */
  async kesintiSablonToggle(s: IKesintiSablon) {
    const gun = this.dialogGun();
    const uid = this.kartUid;
    if (!gun || !uid || this.kesintiKaydediliyor()) return;

    const mevcut = gun.kesintiler?.find(k => k.tur === s.tur && k.baslangic === s.baslangic && k.bitis === s.bitis);
    if (mevcut) {
      // Ekli — tekrar tıklayınca sil
      return this.kesintiSil(mevcut._id);
    }

    this.kesintiKaydediliyor.set(true);
    this.cd.markForCheck();
    try {
      const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.pdks_gun_kesintiler.ekle', gecikmeliCevap: false });
      istek.parametreler = {
        kart_kimlik: uid,
        tarih: gun.tarih,
        tur: s.tur,
        baslangic: s.baslangic,
        bitis: s.bitis,
        aciklama: s.aciklama || '',
      };
      await istek.isteSync();
      await this.ayVerisiYukle(); // dialogGun'ı da güncel veriyle yeniler
    } catch (err) {
      console.error('[KartSahibi] şablon kesintisi eklenemedi', err);
    } finally {
      this.kesintiKaydediliyor.set(false);
      this.cd.markForCheck();
    }
  }

  /** Formdaki değerlerle yeni bir kesinti (mola/izin) ekler. */
  async kesintiEkle() {
    const gun = this.dialogGun();
    const uid = this.kartUid;
    if (!gun || !uid || !this.kesintiBaslangic() || !this.kesintiBitis() || this.kesintiKaydediliyor()) return;
    if (this.kesintiBitis() <= this.kesintiBaslangic()) return; // bitiş, başlangıçtan sonra olmalı

    this.kesintiKaydediliyor.set(true);
    this.cd.markForCheck();
    try {
      const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.pdks_gun_kesintiler.ekle', gecikmeliCevap: false });
      istek.parametreler = {
        kart_kimlik: uid,
        tarih: gun.tarih,
        tur: this.kesintiTur(),
        baslangic: this.kesintiBaslangic(),
        bitis: this.kesintiBitis(),
        aciklama: this.kesintiAciklama().trim(),
      };
      await istek.isteSync();
      this.kesintiFormAcik.set(false);
      await this.ayVerisiYukle();
    } catch (err) {
      console.error('[KartSahibi] kesinti eklenemedi', err);
    } finally {
      this.kesintiKaydediliyor.set(false);
      this.cd.markForCheck();
    }
  }

  /** Var olan bir kesintiyi siler. */
  async kesintiSil(id: string | undefined) {
    if (!id) return;
    try {
      const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.pdks_gun_kesintiler.sil', gecikmeliCevap: false });
      istek.parametreler = { _id: id };
      await istek.isteSync();
      await this.ayVerisiYukle();
    } catch (err) {
      console.error('[KartSahibi] kesinti silinemedi', err);
    }
  }

  /** Var olan bir kesintiyi düzenleme moduna açar — form aynı alanları kullanır. */
  kesintiDuzenleAc(k: IGunKesinti) {
    if (!k._id) return;
    this.kesintiTur.set(k.tur);
    this.kesintiBaslangic.set(k.baslangic);
    this.kesintiBitis.set(k.bitis);
    this.kesintiAciklama.set(k.aciklama || '');
    this.duzenlenenKesintiId.set(k._id);
    this.kesintiFormAcik.set(true);
    this.cd.markForCheck();
  }

  /** Düzenleme modundaki kesintiyi kaydeder. */
  async kesintiDuzenleKaydet() {
    const id = this.duzenlenenKesintiId();
    if (!id || !this.kesintiBaslangic() || !this.kesintiBitis() || this.kesintiKaydediliyor()) return;
    if (this.kesintiBitis() <= this.kesintiBaslangic()) return;

    this.kesintiKaydediliyor.set(true);
    this.cd.markForCheck();
    try {
      const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.pdks_gun_kesintiler.guncelle', gecikmeliCevap: false });
      istek.parametreler = {
        filter: { _id: id },
        set: { tur: this.kesintiTur(), baslangic: this.kesintiBaslangic(), bitis: this.kesintiBitis(), aciklama: this.kesintiAciklama().trim() },
      };
      await istek.isteSync();
      this.kesintiFormAcik.set(false);
      this.duzenlenenKesintiId.set(null);
      await this.ayVerisiYukle();
    } catch (err) {
      console.error('[KartSahibi] kesinti güncellenemedi', err);
    } finally {
      this.kesintiKaydediliyor.set(false);
      this.cd.markForCheck();
    }
  }
}
